/** Shared keys for ProductSelectionModal ↔ PO line items (create + edit). */

const normalizeVariantId = (value) => {
  if (value == null) return "";
  const normalized = String(value).trim();
  return normalized && normalized !== "0" ? normalized : "";
};

export const normalizeUpc = (value) => String(value ?? "").trim();

/** Compare UPCs allowing optional leading-zero differences. */
export const upcsMatch = (a, b) => {
  const left = normalizeUpc(a);
  const right = normalizeUpc(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const stripLeadingZeros = (value) => value.replace(/^0+/, "") || "0";
  return stripLeadingZeros(left) === stripLeadingZeros(right);
};

const upcMapKeys = (upc) => {
  const normalized = normalizeUpc(upc);
  if (!normalized) return [];
  const keys = [`upc:${normalized}`];
  const stripped = normalized.replace(/^0+/, "") || "0";
  if (stripped !== normalized) keys.push(`upc:${stripped}`);
  return keys;
};

export const toVariantKey = (productId, variantToken) =>
  `${String(productId || "").trim()}-${variantToken || "base"}`;

/** Token from a product-list API row (picker modal). */
export const getVariantTokenFromApiRow = (row) => {
  if (row?.varient_id != null && String(row.varient_id).trim() !== "") {
    return String(row.varient_id);
  }
  if (row?.upc != null && String(row.upc).trim() !== "") {
    return `upc-${String(row.upc).trim()}`;
  }
  if (row?.product_name != null && String(row.product_name).trim() !== "") {
    return `name-${String(row.product_name).trim().toLowerCase()}`;
  }
  return "base";
};

/** Token from a PO line item (detail page / create table). */
export const getVariantTokenFromLineItem = (lineItem) => {
  const variantId = normalizeVariantId(lineItem?.variantId ?? lineItem?.variant_id);
  if (variantId) return variantId;
  const upc = lineItem?.sku || lineItem?.upc;
  if (upc != null && String(upc).trim() !== "") {
    return `upc-${String(upc).trim()}`;
  }
  const name = lineItem?.label || lineItem?.title || lineItem?.product_name;
  if (name != null && String(name).trim() !== "") {
    return `name-${String(name).trim().toLowerCase()}`;
  }
  return "base";
};

/** All picker keys that may represent this line item in the modal. */
export const getPickerKeysForLineItem = (lineItem) => {
  const productId = String(lineItem?.productId || lineItem?.product_id || "").trim();
  if (!productId) return [];

  const keys = new Set();
  const primaryToken = getVariantTokenFromLineItem(lineItem);
  keys.add(toVariantKey(productId, primaryToken));

  // Only map the product-level "base" key for lines with no variant identity.
  // Registering "base" for every variant blocked all siblings in the picker.
  if (primaryToken === "base") {
    keys.add(toVariantKey(productId, "base"));
  }

  const variantId = normalizeVariantId(lineItem?.variantId ?? lineItem?.variant_id);
  if (variantId && variantId !== primaryToken) {
    keys.add(toVariantKey(productId, variantId));
  }

  const upc = lineItem?.sku || lineItem?.upc;
  if (upc != null && String(upc).trim() !== "") {
    const upcToken = `upc-${String(upc).trim()}`;
    if (upcToken !== primaryToken) {
      keys.add(toVariantKey(productId, upcToken));
    }
  }

  return [...keys];
};

/** Map keyed for ProductSelectionModal `existing` prop. */
export const buildExistingProductsMapFromLineItems = (lineItems) => {
  const map = {};
  (lineItems || []).forEach((lineItem) => {
    getPickerKeysForLineItem(lineItem).forEach((key) => {
      map[key] = lineItem;
    });
    upcMapKeys(lineItem?.sku || lineItem?.upc).forEach((key) => {
      map[key] = lineItem;
    });
  });
  return map;
};

const lineItemMatchesPickerRow = (lineItem, row, pickerKey) => {
  if (!lineItem) return false;

  const productId = String(row?.product_id || "").trim();
  const lineProductId = String(lineItem.productId || lineItem.product_id || "").trim();
  const rowVariantId =
    row?.varient_id != null ? String(row.varient_id).trim() : "";
  const lineVariantId = normalizeVariantId(
    lineItem.variantId ?? lineItem.variant_id
  );
  const rowUpc = normalizeUpc(row?.upc);
  const lineUpc = normalizeUpc(lineItem.sku || lineItem.upc);

  if (lineProductId && productId && lineProductId !== productId) {
    return false;
  }

  if (rowVariantId && lineVariantId && rowVariantId === lineVariantId) {
    return true;
  }

  if (rowUpc && lineUpc && upcsMatch(rowUpc, lineUpc)) {
    return true;
  }

  if (pickerKey && getPickerKeysForLineItem(lineItem).includes(pickerKey)) {
    return true;
  }

  return false;
};

/** Matching PO line for a product_by_upc / product-list row, if any. */
export const findLineItemForApiRow = (row, lineItems = []) => {
  if (!row || !lineItems.length) return null;
  const existing = buildExistingProductsMapFromLineItems(lineItems);
  if (!isProductRowAlreadyOnPo(row, existing)) return null;

  const productId = String(row?.product_id || "").trim();
  const pickerKey = toVariantKey(productId, getVariantTokenFromApiRow(row));
  const seen = new Set();
  for (const lineItem of Object.values(existing)) {
    if (!lineItem || seen.has(lineItem)) continue;
    seen.add(lineItem);
    if (lineItemMatchesPickerRow(lineItem, row, pickerKey)) return lineItem;
  }
  return null;
};

/** Whether a product-list row is already on the PO. */
export const isProductRowAlreadyOnPo = (row, existing = {}) => {
  const productId = String(row?.product_id || "").trim();
  if (!productId) return false;

  const rowToken = getVariantTokenFromApiRow(row);
  const pickerKey = toVariantKey(productId, rowToken);
  if (existing[pickerKey]) return true;

  if (rowToken === "base") {
    const legacyBaseKey = toVariantKey(productId, "base");
    if (existing[legacyBaseKey]) return true;
  }

  if (upcMapKeys(row?.upc).some((key) => existing[key])) return true;

  const lineItems = new Set(Object.values(existing));
  for (const lineItem of lineItems) {
    if (lineItemMatchesPickerRow(lineItem, row, pickerKey)) return true;
  }

  return false;
};
